import React, { useState, useRef } from "react";
import styled from "@emotion/styled";
import PropTypes from "prop-types";
import { motion } from "framer-motion";

import TabItem from "./TabItem";
import Slide from "./Slide";

// style rules
import { Sizes, Colors } from "../../styles";

const SlideToggles = ({ slides, selectedSlide, onToggle }) => {
	if (!slides || slides.length === 0) return null;

	return (
		<ToggleSection>
			{slides.map((slide) => (
				<TabItem
					key={slide.title ?? slide.id ?? JSON.stringify(slide)}
					filter={slide}
					isActive={slide.title === selectedSlide}
					handleFilterSelect={onToggle}
				/>
			))}
		</ToggleSection>
	);
};

SlideToggles.propTypes = {
	slides: PropTypes.array,
	selectedSlide: PropTypes.string,
	onToggle: PropTypes.func,
};

const SlidesContent = ({ slides, selectedSlide }) => {
	if (!slides || slides.length === 0) return null;

	const slideToDisplay = slides.find((slide) => slide.title === selectedSlide) || slides[0];
	if (!slideToDisplay) return null;

	return <Slide slide={slideToDisplay} />;
};

SlidesContent.propTypes = {
	slides: PropTypes.array,
	selectedSlide: PropTypes.string,
};

const App = ({ tabsJson }) => {
	const [slides] = useState(JSON.parse(tabsJson));
	const [selectedSlide, setSelectedSlide] = useState(slides[0].title);

	// used for animating the slides on toggle
	const ref = useRef(0);

	const toggleSlide = (slide) => {
		ref.current++; // used for triggering animation
		return setSelectedSlide(slide.title);
	};

	return (
		<>
			<SlideToggles
				slides={slides}
				selectedSlide={selectedSlide}
				onToggle={toggleSlide}
			/>

			<SlidesContainer
				animate={ref.current === 0 ? {} : { opacity: [0, 1] }}
				transition={{ duration: 0.7 }}
				key={ref.current}
			>
				<SlidesContent slides={slides} selectedSlide={selectedSlide} />
			</SlidesContainer>
		</>
	);
};

App.propTypes = {
	tabsJson: PropTypes.string,
};

export default App;

const SlidesContainer = styled(motion.div)`
	display: flex;
	flex-direction: column;
	gap: 7rem;
	border-radius: 0.5rem;
	background-color: ${Colors.fp.pineGreen60};
	position: relative;
	width: 100vw;
	left: 50%;
	right: 50%;
	margin-left: -50vw;
	margin-right: -50vw;
	text-align: center;
	max-width: unset;
	
	@media only screen and (min-width: ${Sizes.screens.medium}) {
		position: unset;
		width: unset;
		left: unset;
		right: unset;
		margin-left: unset;
		margin-right: unset;
		text-align: unset;
		gap: unset;
		margin-top: 5rem;
		border-radius: 1rem;
	}
`;

const ToggleSection = styled.ul`
	margin: 0;
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	grid-gap: 1rem;
	padding-left: 0;

	@media only screen and (min-width: ${Sizes.screens.medium}) {
		display: flex;
		gap: 2rem;
		justify-content: center;
		flex-wrap: wrap;
		padding-left: 0;
	}
`;
